import React, { useContext, useEffect, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { selectCharacter, selectEquipment, selectCombat, selectLoading, fetchCharacterInventory } from '../ViewInventory/inventorySlice'
import CharacterPortraitPanel from './components/CharacterPortraitPanel'
import TravelPanel from '../../../features/travel/TravelPanel'
import SheetReviewPanel from './components/SheetReviewPanel'
import GraduationRequirementsPanel from './components/GraduationRequirementsPanel'
import WoundsPanel from './components/WoundsPanel'
import StatsReallocModal from './components/StatsReallocModal'
import LevelUpStatPointsPanel from './components/LevelUpStatPointsPanel'
import { useSheetReviewContext } from './components/SheetReviewContext'
import AppContext from '../../../contexts/AppContext'
import {
  combatAttrsForParent,
  combatStatsFromCharacter,
  computeCombatAttributes,
  computeDerivedPVCK,
  formatDerivedStat,
} from '../shared/characterDerivedStats'

const STAT_ORDER = ['strength', 'dexterity', 'agility', 'resistance', 'willpower', 'chakraControl', 'concentration', 'intelligence']

const STAT_LABEL = {
  strength: 'Fuerza',
  dexterity: 'Destreza',
  agility: 'Agilidad',
  resistance: 'Resistencia',
  willpower: 'Voluntad',
  chakraControl: 'Control de Chakra',
  concentration: 'Concentración',
  intelligence: 'Inteligencia',
}

const STAT_ACRONYM = {
  strength: 'FUE',
  dexterity: 'DES',
  agility: 'AGI',
  resistance: 'RES',
  willpower: 'VOL',
  chakraControl: 'CdC',
  concentration: 'CON',
  intelligence: 'INT',
}

const COMBAT_STATS = [
  { key: 'range', label: 'Alcance', suffix: ' m', getValue: (combat) => Number(combat?.range || 0) },
  { key: 'damage', label: 'Daño', getValue: (_, getTotalDamage) => getTotalDamage() },
  { key: 'critical', label: 'Crítico', prefix: '×', getValue: (combat) => Number(combat?.critical || 1) },
  { key: 'weaponBonus', label: 'Bon. armas', prefix: '+', getValue: (combat) => Number(combat?.weaponBonus || 0) },
]

export default function CharacterFicha() {
  const { slug } = useParams()
  const dispatch = useDispatch()
  const { refreshActiveCharacter } = useContext(AppContext) || {}
  const loading = useSelector(selectLoading)
  const character = useSelector(selectCharacter)
  const equipment = useSelector(selectEquipment)
  const combat = useSelector(selectCombat)
  const { showReviewUi, sheetStatus } = useSheetReviewContext()

  useEffect(() => {
    if (slug) {
      dispatch(fetchCharacterInventory(slug))
    }
  }, [slug, dispatch])

  const char = character || {}
  const isAcademy = Boolean(char.academyMode)
  const needsStatsRealloc = Boolean(char.statsReallocPending) && !isAcademy
  const unspentStatPoints = Math.max(0, Number(char.unspentStatPoints) || 0)
  const showLevelUpPoints = !isAcademy && !needsStatsRealloc && unspentStatPoints > 0
  const expCurrent = Number(char?.experience?.current || 0)
  const expMax = Number(char?.experience?.max || 0)
  const expPct = expMax > 0 ? Math.min(100, Math.round((expCurrent / expMax) * 100)) : 0

  const { PV, CK, combatAttrs } = useMemo(() => {
    const stats = combatStatsFromCharacter(char)
    const level = Math.max(1, Number(char.level) || 1)
    const derived = computeDerivedPVCK(stats, level, 0, 0)
    return { ...derived, combatAttrs: computeCombatAttributes(stats) }
  }, [char])

  // Mientras hay revisión pendiente, solo el panel de corrección (no el pergamino)
  const hideOrnateSheet = showReviewUi && sheetStatus && sheetStatus !== 'approved'

  const getTotalDamage = () => {
    let minDamage = Number(combat?.damage?.min || 0)
    let maxDamage = Number(combat?.damage?.max || 0)
    if (equipment?.mainHand?.damage) {
      const parts = String(equipment.mainHand.damage)
        .split('-')
        .map((n) => Number(n))
      const [min, max] = [parts[0] || 0, parts[1] || 0]
      minDamage += min
      maxDamage += max
    }
    return `${minDamage}–${maxDamage}`
  }

  if (loading && !char?.name) {
    return (
      <div className="char-ficha">
        <div className="char-ficha__loading">
          <span className="char-ficha__loading-spinner" aria-hidden="true" />
          <p>Cargando ficha del shinobi…</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`char-ficha${hideOrnateSheet ? ' char-ficha--review-only' : ''}${
        isAcademy ? ' char-ficha--academy' : ''
      }`}
    >
      {needsStatsRealloc ? (
        <StatsReallocModal
          slug={slug}
          level={Number(char.level || 10)}
          onComplete={() => {
            dispatch(fetchCharacterInventory(slug))
            if (typeof refreshActiveCharacter === 'function') {
              refreshActiveCharacter()
            }
          }}
        />
      ) : null}
      <SheetReviewPanel />
      {!hideOrnateSheet ? (
        <div className="char-sheet char-sheet--ornate" aria-label="Ficha de personaje">
          <div className="char-sheet-grid">
            <CharacterPortraitPanel char={char} slug={slug} expPct={expPct} />

            <section aria-label="Bloques de ficha" className="char-right-panel">
              {isAcademy ? <GraduationRequirementsPanel character={char} /> : null}

              {!isAcademy ? (
                <div className="char-stats-stack">
                  <article className="char-card char-card--section char-card--stats-primary">
                    <header className="char-section-header">
                      <span className="char-section-header__ornament" aria-hidden="true" />
                      <h3 className="char-section-header__title">Atributos</h3>
                      <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
                    </header>

                    {unspentStatPoints > 0 && !needsStatsRealloc ? (
                      <p className="char-attr-note" role="status">
                        Tienes <strong>{unspentStatPoints}</strong> punto
                        {unspentStatPoints === 1 ? '' : 's'} de atributo por subir de nivel.
                        Repártelos abajo.
                      </p>
                    ) : null}

                    <div className="char-attr-grid char-attr-grid--cards char-attr-grid--compact">
                      {STAT_ORDER.map((s) => {
                        const st = char?.stats?.[s] || { base: 0, bonus: 0 }
                        const base = Number(st.base || 0)
                        const bonus = Number(st.bonus || 0)
                        const total = base + bonus
                        const barPct = `${(Math.max(0, Math.min(10, base)) / 10) * 100}%`
                        const derived = combatAttrsForParent(s)

                        return (
                          <div
                            key={s}
                            className={`char-attr-card char-attr-card--compact${
                              derived.length ? ' char-attr-card--has-derived' : ''
                            }`}
                          >
                            <div className="char-attr-card__head">
                              <span className="char-attr__acronym">{STAT_ACRONYM[s]}</span>
                              <span className="char-attr__name">{STAT_LABEL[s]}</span>
                              <span
                                className="char-attr__total"
                                title={`Base ${base}${bonus ? ` · Bon. ${bonus >= 0 ? `+${bonus}` : bonus}` : ''}`}
                              >
                                {total}
                              </span>
                            </div>

                            <div className="char-attr-bar" aria-label={`Barra ${STAT_LABEL[s]}: ${base} de 10`}>
                              <div className="char-attr-bar__fill" style={{ width: barPct }} />
                            </div>

                            <div className="char-attr-card__meta">
                              <span>Base {base}</span>
                              <span className={bonus > 0 ? 'positive' : bonus < 0 ? 'negative' : ''}>
                                Bon. {bonus >= 0 ? `+${bonus}` : bonus}
                              </span>
                            </div>

                            {derived.length ? (
                              <ul className="char-attr-derived" aria-label={`Derivados de ${STAT_LABEL[s]}`}>
                                {derived.map(({ key, label, unit, hint }) => (
                                  <li
                                    key={key}
                                    className="char-attr-derived__row"
                                    title={hint || undefined}
                                  >
                                    <span className="char-attr-derived__label">{label}</span>
                                    <span className="char-attr-derived__value">
                                      {formatDerivedStat(combatAttrs[key])}
                                      {unit ? (
                                        <span className="char-attr-derived__unit">{unit}</span>
                                      ) : null}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  </article>
                </div>
              ) : null}

              {showLevelUpPoints ? (
                <LevelUpStatPointsPanel
                  slug={slug}
                  unspent={unspentStatPoints}
                  currentStats={char.stats}
                  onComplete={() => {
                    dispatch(fetchCharacterInventory(slug))
                    if (typeof refreshActiveCharacter === 'function') {
                      refreshActiveCharacter()
                    }
                  }}
                />
              ) : null}

              {!isAcademy ? (
                <div className="char-bottom-row">
                  <article className="char-card char-card--section char-card--compact">
                    <header className="char-section-header">
                      <span className="char-section-header__ornament" aria-hidden="true" />
                      <h3 className="char-section-header__title">Vitales y arma</h3>
                      <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
                    </header>

                    <div className="char-vitals" aria-label="Puntos de vida y chakra">
                      <div className="char-vital char-vital--hp">
                        <span className="char-vital__label">PV</span>
                        <span className="char-vital__value">{PV}</span>
                        <span className="char-vital__hint">Puntos de vida</span>
                      </div>
                      <div className="char-vital char-vital--ck">
                        <span className="char-vital__label">CK</span>
                        <span className="char-vital__value">{CK}</span>
                        <span className="char-vital__hint">Chakra</span>
                      </div>
                    </div>

                    <div className="char-combat-grid">
                      {COMBAT_STATS.map(({ key, label, prefix = '', suffix = '', getValue }) => (
                        <div key={key} className="char-combat-stat">
                          <span className="char-combat-stat__label">{label}</span>
                          <span className="char-combat-stat__value">
                            {prefix}
                            {getValue(combat, getTotalDamage)}
                            {suffix}
                          </span>
                        </div>
                      ))}
                    </div>
                  </article>

                  <article className="char-card char-card--section char-card--compact">
                    <header className="char-section-header">
                      <span className="char-section-header__ornament" aria-hidden="true" />
                      <h3 className="char-section-header__title">Equipo</h3>
                      <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
                    </header>

                    <div className="char-equip-summary">
                      <div className="char-weapon-slot">
                        <span className="char-weapon-slot__label">Mano principal</span>
                        <span className={`char-weapon-slot__name ${equipment?.mainHand ? '' : 'empty'}`}>
                          {equipment?.mainHand?.name || 'Vacío'}
                        </span>
                      </div>
                      <div
                        className={`char-weapon-slot${
                          equipment?.mainHand?.grip === 'twoHand' ? ' char-weapon-slot--disabled' : ''
                        }`}
                        title={
                          equipment?.mainHand?.grip === 'twoHand'
                            ? 'Ocupada por arma a dos manos'
                            : undefined
                        }
                      >
                        <span className="char-weapon-slot__label">Secundaria</span>
                        <span
                          className={`char-weapon-slot__name ${
                            equipment?.mainHand?.grip === 'twoHand' || !equipment?.offHand ? 'empty' : ''
                          }`}
                        >
                          {equipment?.mainHand?.grip === 'twoHand'
                            ? 'Dos manos'
                            : equipment?.offHand?.name || 'Vacío'}
                        </span>
                      </div>
                      <Link to={`/personajes/${slug}/equipo`} className="char-btn-accent char-btn-accent--full">
                        Abrir equipo e inventario
                      </Link>
                    </div>
                  </article>
                </div>
              ) : null}

              {!isAcademy ? <WoundsPanel /> : null}

              {!isAcademy ? (
                <TravelPanel characterId={char?.id} homeSlug={char?.currentTerritorySlug || null} mode="monitor" />
              ) : null}
            </section>

            <div className="char-cta-band char-cta-band--sheet" aria-label="Acciones rápidas">
              {!isAcademy ? (
                <Link to={`/personajes/${slug}/progresion`} className="char-btn char-btn--ghost">
                  Ver progresión
                </Link>
              ) : null}
              <Link to={`/personajes/${slug}/historia`} className="char-btn char-btn--ghost">
                Editar biografía
              </Link>
              <Link to={`/personajes/${slug}/tecnicas`} className="char-btn char-btn--ghost">
                {isAcademy ? 'Técnicas básicas' : 'Gestionar técnicas'}
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
